import type { SepaQrData } from './sepa-qr'

/**
 * Display mode options
 * - full: Complete interface with form
 * - share: QR code with payment summary (for sharing)
 * - clean: Just QR code (for embedding as image)
 */
export type DisplayMode = 'full' | 'share' | 'clean'

/**
 * Parsed URL parameters
 */
export interface ParsedUrlParams {
  data: Partial<SepaQrData>
  mode: DisplayMode
  hasParams: boolean
}

/**
 * Parse URL search parameters into SEPA QR data
 */
export function parseUrlParams(search: string = window.location.search): ParsedUrlParams {
  const params = new URLSearchParams(search)
  const data: Partial<SepaQrData> = {}
  let mode: DisplayMode = 'full'
  let hasParams = false

  // Parse name
  const name = params.get('name')
  if (name) {
    data.name = decodeURIComponent(name)
    hasParams = true
  }

  // Parse IBAN
  const iban = params.get('iban')
  if (iban) {
    data.iban = decodeURIComponent(iban).replace(/\s/g, '').toUpperCase()
    hasParams = true
  }

  // Parse BIC
  const bic = params.get('bic')
  if (bic) {
    data.bic = decodeURIComponent(bic).toUpperCase()
    hasParams = true
  }

  // Parse amount
  const amountStr = params.get('amount')
  if (amountStr) {
    const amount = parseFloat(amountStr)
    if (!isNaN(amount) && amount > 0) {
      data.amount = amount
    }
    hasParams = true
  }

  // Parse purpose
  const purpose = params.get('purpose')
  if (purpose) {
    data.purpose = decodeURIComponent(purpose).toUpperCase().slice(0, 4)
    hasParams = true
  }

  // Parse reference (mutually exclusive with text)
  const ref = params.get('ref') || params.get('reference')
  if (ref) {
    data.reference = decodeURIComponent(ref)
    hasParams = true
  }

  // Parse text (only if no reference)
  const text = params.get('text')
  if (text && !data.reference) {
    data.text = decodeURIComponent(text)
    hasParams = true
  }

  // Parse information
  const info = params.get('info') || params.get('information')
  if (info) {
    data.information = decodeURIComponent(info)
    hasParams = true
  }

  // Always use UTF-8 (charset code 1)
  data.charset = 1

  // Parse instant flag
  const instantStr = params.get('instant')
  if (instantStr) {
    data.instant = instantStr === 'true' || instantStr === '1'
    hasParams = true
  }

  // Parse display mode
  const modeParam = params.get('mode')
  if (modeParam === 'clean') {
    mode = 'clean'
  } else if (modeParam === 'share') {
    mode = 'share'
  }

  return { data, mode, hasParams }
}

/**
 * Build URL with SEPA QR parameters
 */
export function buildUrl(
  data: Partial<SepaQrData>,
  baseUrl: string = window.location.origin,
  mode?: DisplayMode
): string {
  const url = new URL(baseUrl)
  const params = url.searchParams

  if (data.name) params.set('name', data.name)
  if (data.iban) params.set('iban', data.iban)
  if (data.bic) params.set('bic', data.bic)
  if (data.amount) params.set('amount', data.amount.toString())
  if (data.purpose) params.set('purpose', data.purpose)
  if (data.reference) params.set('ref', data.reference)
  if (data.text) params.set('text', data.text)
  if (data.information) params.set('info', data.information)
  if (data.instant) params.set('instant', 'true')
  if (mode && mode !== 'full') params.set('mode', mode)

  return url.toString()
}

/**
 * Escape HTML attribute value to prevent injection
 */
function escapeHtmlAttr(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Build embed HTML snippet (sanitized)
 */
export function buildEmbedSnippet(imageUrl: string, alt: string = 'SEPA QR Code'): string {
  const safeUrl = escapeHtmlAttr(imageUrl)
  const safeAlt = escapeHtmlAttr(alt)
  return `<img src="${safeUrl}" alt="${safeAlt}" width="256" height="256" />`
}

/**
 * Build iframe embed snippet (sanitized)
 */
export function buildIframeSnippet(url: string, size: number = 300): string {
  const safeUrl = escapeHtmlAttr(url)
  const safeSize = Math.abs(Math.floor(size)) // Ensure positive integer
  return `<iframe src="${safeUrl}&mode=clean" width="${safeSize}" height="${safeSize}" frameborder="0"></iframe>`
}

